/**
 * RIFT — map vote.
 *
 * The screen between picking a playlist and deploying: every registered map on a
 * card, one vote each, highest tally wins. Self-contained like `mainmenu.js` —
 * one class, its own scoped `.rv-` stylesheet, and no assets. The plan view on
 * each card is drawn from the map's own layout data (building footprints, alleys,
 * spawns), so a new map gets a preview for free and nobody has to draw one.
 *
 *     const vote = new MapVote(host, {
 *       maps, activeId, session: 'offline' | 'online', duration: 20,
 *       onConfirm: (id) => {},   // the map that won
 *       onCancel: () => {},
 *     });
 *     vote.show();
 *
 * OFFLINE is a straight choice: one player, one vote, no timer worth running.
 * ONLINE is a real tally — `castVote()` reports the local pick, `setTally()`
 * feeds in what the server says everyone else picked, and the clock decides. The
 * winner is resolved here only so the screen can close on time; the server
 * remains the authority and `setResult()` overrides anything shown.
 */

const STYLE_ID = 'rv-mapvote-style';

/** Picks a map for the player who does not care which one. */
const RANDOM_ID = '__random__';

/**
 * Card art: one WebP per map, keyed by id, built from that map's hero capture by
 * `tools/mapthumbs.mjs`. Vite hashes and inlines the URLs at build time, so this
 * costs one `<img>` per card and no runtime lookup.
 *
 * EAGER, unlike the map registry's own glob — these are ~30 KB each and all of
 * them are on screen at once, so a lazy import would only add six round trips to
 * the first paint of the screen that needs them.
 *
 * A map with no capture yet is not an error: `THUMBS[id]` is undefined, no
 * `<img>` is created, and the card keeps the generated plan view underneath.
 */
const THUMBS = (() => {
  const out = {};
  try {
    const files = import.meta.glob('./mapthumbs/*.webp', { eager: true, query: '?url', import: 'default' });
    for (const [path, url] of Object.entries(files)) {
      const id = /([^/]+)\.webp$/.exec(path)?.[1];
      if (id) out[id] = url;
    }
  } catch {
    // No bundler (a raw ES-module page, or a test importing this file directly).
    // Plan views still draw, so the screen is fully usable without art.
  }
  return out;
})();

export class MapVote {
  constructor(host, opts = {}) {
    this.host = host || document.body;

    const all = (opts.maps ?? []).filter((m) => m?.id);
    const activeId = opts.activeId ?? null;
    // Every card on the ballot carries a capture of its own map. A map with no
    // thumbnail yet is held back rather than shown as the one drawing in a row
    // of photographs — and it returns on its own the moment
    // `tools/mapthumbs.py` has art for it, so this needs no list to maintain.
    //
    // The map already loaded is ALWAYS kept, art or not. It is the one pick that
    // costs nothing — `_applyMapChoice` starts it on the spot, with no reload
    // and no engine re-boot. Holding it back (the default map, `market`, has no
    // capture) hid that path entirely: it left no card marked LOADED and made
    // every single choice a full page reload.
    const keep = (m) => Boolean(THUMBS[m.id]) || m.id === activeId;
    const withArt = all.filter(keep);
    const held = all.filter((m) => !keep(m));
    // ...unless holding them back would leave nothing worth voting on. A ballot
    // of plan views beats a ballot of one, so the rule yields rather than
    // emptying the screen on a checkout with no captures in it.
    const holding = withArt.length >= 2;
    this.maps = holding ? withArt : all;
    if (holding && held.length) {
      console.info(
        `[mapvote] held back, no card art: ${held.map((m) => m.id).join(', ')} — run tools/mapthumbs.py`
      );
    }
    this.activeId = activeId;
    this.session = opts.session === 'online' ? 'online' : 'offline';
    this.duration = Math.max(0, opts.duration ?? (this.session === 'online' ? 20 : 0));
    this.onConfirm = opts.onConfirm ?? null;
    this.onCancel = opts.onCancel ?? null;
    this.onVote = opts.onVote ?? null;

    this.visible = false;
    this.index = Math.max(0, this.maps.findIndex((m) => m.id === this.activeId));
    this.myVote = null;
    /** id -> vote count, including this client's. */
    this.tally = new Map();
    this.locked = false;
    this._raf = 0;
    this._deadline = 0;
    this._cards = [];

    this._injectStyle();
    this._build();
    this._wire();
  }

  /* ==================================================================== */
  /* dom                                                                  */
  /* ==================================================================== */

  _el(tag, cls, parent, text) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    if (parent) parent.appendChild(n);
    return n;
  }

  _injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    const s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  _build() {
    this.root = this._el('section', 'rv-root', null);
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', 'Map vote');
    this.root.hidden = true;

    const head = this._el('div', 'rv-head', this.root);
    const headL = this._el('div', null, head);
    this._el('div', 'rv-eyebrow', headL, this.session === 'online' ? 'LOBBY VOTE' : 'MAP SELECT');
    this.title = this._el('h1', 'rv-title', headL, 'WHERE ARE WE FIGHTING?');
    this.copy = this._el(
      'p',
      'rv-copy',
      headL,
      this.session === 'online'
        ? 'Every operator gets one vote. The map with the most votes deploys.'
        : 'Pick a combat environment. The map loads when you deploy.'
    );

    const clock = this._el('div', 'rv-clock', head);
    this.clockRing = this._el('div', 'rv-clock-ring', clock);
    this.clockNum = this._el('span', 'rv-clock-num', this.clockRing, '--');
    this._el('span', 'rv-clock-lbl', clock, this.session === 'online' ? 'VOTING' : 'NO TIMER');
    if (this.session !== 'online') clock.classList.add('rv-clock-idle');

    // A ballot worth scrolling more than a screenful is a ballot worth
    // searching. Below that this would just be one more control to look at
    // for no benefit, so it only exists once there is something to filter.
    this.search = null;
    if (this.maps.length > 8) {
      const bar = this._el('div', 'rv-filter', this.root);
      this.search = this._el('input', 'rv-search', bar);
      this.search.type = 'search';
      this.search.placeholder = 'Filter by name, theme or size…';
      this.search.setAttribute('aria-label', 'Filter maps');
      this.search.addEventListener('input', () => this._applyFilter());
    }

    this.grid = this._el('div', 'rv-grid', this.root);
    for (let i = 0; i < this.maps.length; i++) this._card(this.maps[i], i);
    this._randomCard(this.maps.length);
    this.noMatches = this._el('div', 'rv-no-matches', this.root, 'No maps match that filter.');
    this.noMatches.hidden = true;

    const foot = this._el('div', 'rv-foot', this.root);
    this.back = this._el('button', 'rv-back', foot, 'Back');
    this.back.type = 'button';
    this.back.addEventListener('click', () => this._cancel());

    const hint = this._el('div', 'rv-hint', foot);
    this._el('span', 'rv-key', hint, 'A');
    this._el('span', 'rv-key', hint, 'D');
    this._el('span', 'rv-hint-t', hint, 'BROWSE');
    this._el('span', 'rv-key', hint, '↵');
    this._el('span', 'rv-hint-t', hint, this.session === 'online' ? 'VOTE' : 'DEPLOY');

    this.confirm = this._el('button', 'rv-confirm', foot, this.session === 'online' ? 'CAST VOTE' : 'DEPLOY');
    this.confirm.type = 'button';
    this.confirm.addEventListener('click', () => this._commit());

    this.host.appendChild(this.root);
    this._select(this.index, false);
  }

  _card(map, i) {
    const c = this._el('button', 'rv-card', this.grid);
    c.type = 'button';
    c.dataset.id = map.id;
    c.style.setProperty('--i', String(i));

    const art = this._el('div', 'rv-art', c);
    const cv = this._el('canvas', 'rv-plan', art);
    cv.width = 320;
    cv.height = 200;
    drawPlan(cv, map);

    // The capture sits OVER the plan rather than replacing it, so the plan is
    // the fallback for free: a map with no art, or art that fails to decode,
    // degrades to the drawing instead of to an empty box. It fades in on load
    // so a slow decode never shows a half-painted card.
    const thumb = THUMBS[map.id];
    if (thumb) {
      const img = this._el('img', 'rv-photo', art);
      img.decoding = 'async';
      // Decorative: the map's name is already read out from `.rv-name` below,
      // so announcing the picture too would just repeat it.
      img.alt = '';
      img.addEventListener('load', () => img.classList.add('rv-photo-on'));
      img.addEventListener('error', () => img.remove());
      img.src = thumb;
    }

    // A one-word read on the light: a night map and a noon map should not look
    // interchangeable on a card.
    this._el('span', 'rv-hour', art, hourLabel(map.hour));
    if (map.id === this.activeId) this._el('span', 'rv-loaded', art, 'LOADED');

    const body = this._el('div', 'rv-body', c);
    const top = this._el('div', 'rv-card-top', body);
    this._el('span', 'rv-name', top, String(map.name ?? map.id).toUpperCase());
    this._el('span', 'rv-size', top, String(map.size ?? '').toUpperCase());
    this._el('span', 'rv-theme', body, map.theme ?? '');
    this._el('span', 'rv-play', body, map.playstyle ?? map.blurb ?? '');

    const bar = this._el('div', 'rv-tally', body);
    const fill = this._el('i', null, bar);
    const count = this._el('span', 'rv-count', body, '');
    if (this.session !== 'online') bar.classList.add('rv-tally-off');

    c.addEventListener('mouseenter', () => this._select(i, false));
    c.addEventListener('focus', () => this._select(i, false));
    c.addEventListener('click', () => {
      this._select(i, false);
      this._commit();
    });

    this._cards.push({ el: c, id: map.id, fill, count });
    return c;
  }

  _randomCard(i) {
    const c = this._el('button', 'rv-card rv-card-random', this.grid);
    c.type = 'button';
    c.dataset.id = RANDOM_ID;
    c.style.setProperty('--i', String(i));
    const art = this._el('div', 'rv-art', c);
    this._el('div', 'rv-dice', art, '?');
    const body = this._el('div', 'rv-body', c);
    const top = this._el('div', 'rv-card-top', body);
    this._el('span', 'rv-name', top, 'RANDOM');
    this._el('span', 'rv-theme', body, 'Let the lobby decide');
    this._el('span', 'rv-play', body, 'Any environment, drawn when voting closes.');
    const bar = this._el('div', 'rv-tally', body);
    const fill = this._el('i', null, bar);
    const count = this._el('span', 'rv-count', body, '');
    if (this.session !== 'online') bar.classList.add('rv-tally-off');
    c.addEventListener('mouseenter', () => this._select(i, false));
    c.addEventListener('focus', () => this._select(i, false));
    c.addEventListener('click', () => {
      this._select(i, false);
      this._commit();
    });
    this._cards.push({ el: c, id: RANDOM_ID, fill, count });
  }

  /* ==================================================================== */
  /* interaction                                                          */
  /* ==================================================================== */

  _wire() {
    this._onKey = (e) => {
      if (!this.visible) return;
      const n = this._cards.length;
      const k = e.key;
      if (k === 'ArrowRight' || k === 'd' || k === 'D') {
        this._select((this.index + 1) % n, true);
        e.preventDefault();
      } else if (k === 'ArrowLeft' || k === 'a' || k === 'A') {
        this._select((this.index - 1 + n) % n, true);
        e.preventDefault();
      } else if (k === 'Enter' || k === ' ') {
        this._commit();
        e.preventDefault();
      } else if (k === 'Escape') {
        this._cancel();
        e.preventDefault();
      }
      // Everything else stays inside this dialog rather than driving the menu
      // underneath it.
      e.stopPropagation();
    };
    // CAPTURE phase, deliberately. `MainMenu` also listens on `document`, and it
    // registered first — in the bubble phase it would therefore see every key
    // before this screen did, and `stopPropagation` here would be too late to
    // matter. Arrow keys would walk the menu's hidden flow buttons while the
    // ballot was open, and Escape would close both screens at once.
    document.addEventListener('keydown', this._onKey, true);
  }

  _select(i, focus) {
    this.index = i;
    for (let k = 0; k < this._cards.length; k++) {
      this._cards[k].el.classList.toggle('rv-on', k === i);
    }
    if (focus) this._cards[i]?.el.focus({ preventScroll: true });
  }

  /** Name/theme/size/blurb substring match. The RANDOM card is always shown. */
  _applyFilter() {
    const q = (this.search?.value ?? '').trim().toLowerCase();
    let visible = 0;
    for (const card of this._cards) {
      if (card.id === RANDOM_ID) {
        visible++;
        continue;
      }
      const map = this.maps.find((m) => m.id === card.id);
      const hay = `${map?.name ?? ''} ${map?.theme ?? ''} ${map?.size ?? ''} ${map?.blurb ?? ''}`.toLowerCase();
      const match = !q || hay.includes(q);
      card.el.hidden = !match;
      if (match) visible++;
    }
    this.noMatches.hidden = visible > 0;
  }

  /**
   * Cast, or deploy. Offline this is the whole decision. Online it registers one
   * vote and then waits for the clock — a second press changes the vote, which is
   * what every player expects a vote screen to let them do.
   */
  _commit() {
    const pick = this._cards[this.index];
    if (!pick || this.locked) return;

    this.myVote = pick.id;
    for (const c of this._cards) c.el.classList.toggle('rv-voted', c.id === pick.id);

    if (this.session !== 'online') {
      this.locked = true;
      this._finish(pick.id);
      return;
    }

    // Show the local vote immediately. The server's tally replaces this the
    // moment it arrives, so a lost packet cannot leave the bar wrong forever.
    this.tally.set(pick.id, Math.max(1, this.tally.get(pick.id) ?? 0));
    this._renderTally();
    this.confirm.textContent = 'CHANGE VOTE';
    try {
      this.onVote?.(pick.id);
    } catch (err) {
      console.warn('[mapvote] vote handler threw', err);
    }
  }

  _cancel() {
    if (this.locked) return;
    this.hide();
    try {
      this.onCancel?.();
    } catch (err) {
      console.warn('[mapvote] cancel handler threw', err);
    }
  }

  /** The map that won, resolved and handed up. Called once. */
  _finish(id) {
    const winner = id === RANDOM_ID ? this._randomPick() : id;
    this._stopClock();
    try {
      this.onConfirm?.(winner);
    } catch (err) {
      console.warn('[mapvote] confirm handler threw', err);
    }
  }

  _randomPick() {
    const pool = this.maps.filter((m) => m.id !== this.activeId);
    const list = pool.length ? pool : this.maps;
    if (!list.length) return this.activeId;
    return list[Math.floor(Math.random() * list.length)].id;
  }

  /* ==================================================================== */
  /* the tally                                                            */
  /* ==================================================================== */

  /**
   * Feed in the lobby's votes: `{ mapId: count }`, or a Map. The server owns
   * this number — including this client's own vote — so it replaces rather than
   * adds to whatever is on screen.
   */
  setTally(counts) {
    this.tally = counts instanceof Map ? new Map(counts) : new Map(Object.entries(counts ?? {}));
    this._renderTally();
  }

  /** The server has decided. Overrides the local resolution and closes. */
  setResult(id) {
    if (this.locked) return;
    this.locked = true;
    for (const c of this._cards) c.el.classList.toggle('rv-won', c.id === id);
    this._finish(id);
  }

  _renderTally() {
    let total = 0;
    let top = 0;
    for (const v of this.tally.values()) {
      total += v;
      if (v > top) top = v;
    }
    for (const c of this._cards) {
      const v = this.tally.get(c.id) ?? 0;
      c.fill.style.width = top > 0 ? ((v / top) * 100).toFixed(1) + '%' : '0%';
      c.count.textContent = v > 0 ? `${v} VOTE${v === 1 ? '' : 'S'}` : '';
      c.el.classList.toggle('rv-lead', v > 0 && v === top);
    }
    this.copy.textContent =
      this.session === 'online'
        ? total > 0
          ? `${total} vote${total === 1 ? '' : 's'} cast. The map with the most votes deploys.`
          : 'Every operator gets one vote. The map with the most votes deploys.'
        : this.copy.textContent;
  }

  /**
   * Whoever is ahead when the clock runs out. Ties break by the order the maps
   * are registered in, which is stable across clients — two players resolving the
   * same tally must never pick different maps.
   */
  _leader() {
    let best = null;
    let bestN = -1;
    for (const c of this._cards) {
      const v = this.tally.get(c.id) ?? 0;
      if (v > bestN) {
        bestN = v;
        best = c.id;
      }
    }
    if (bestN <= 0) return this.myVote ?? this.maps[0]?.id ?? this.activeId;
    return best;
  }

  /* ==================================================================== */
  /* lifecycle                                                            */
  /* ==================================================================== */

  show() {
    if (this.visible) return;
    this.visible = true;
    this.locked = false;
    this.root.hidden = false;
    void this.root.offsetWidth;
    this.root.classList.add('rv-live');
    if (this.session === 'online' && this.duration > 0) this._startClock();
    else this.clockNum.textContent = '--';
    setTimeout(() => this._cards[this.index]?.el.focus({ preventScroll: true }), 60);
  }

  hide() {
    if (!this.visible) return;
    this.visible = false;
    this.root.classList.remove('rv-live');
    this.root.hidden = true;
    this._stopClock();
  }

  _startClock() {
    this._deadline = performance.now() + this.duration * 1000;
    const tick = () => {
      if (!this.visible) return;
      const left = Math.max(0, this._deadline - performance.now());
      const s = Math.ceil(left / 1000);
      this.clockNum.textContent = String(s).padStart(2, '0');
      this.clockRing.style.setProperty('--p', String(1 - left / (this.duration * 1000)));
      if (left <= 0) {
        this._raf = 0;
        if (!this.locked) {
          this.locked = true;
          this._finish(this._leader());
        }
        return;
      }
      this._raf = requestAnimationFrame(tick);
    };
    this._raf = requestAnimationFrame(tick);
  }

  _stopClock() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = 0;
  }

  dispose() {
    this.hide();
    try {
      document.removeEventListener('keydown', this._onKey, true);
    } catch {
      /* already gone */
    }
    try {
      this.root?.remove();
    } catch {
      /* already gone */
    }
  }
}

/* ====================================================================== */
/* the plan view                                                          */
/* ====================================================================== */

/**
 * Draw a map's plan from its own spec: building footprints, alleys, the street
 * strip and the spawns. Every map gets a preview with no work from its author
 * and no image file, and because it is generated from the same numbers the level
 * is built from, it cannot go stale the way a hand-drawn thumbnail would.
 */
function drawPlan(canvas, map) {
  const g = canvas.getContext('2d');
  if (!g) return;
  const W = canvas.width;
  const H = canvas.height;

  // Fit the level's own bounds, falling back to the extent of its buildings.
  const b = planBounds(map);
  const pad = 10;
  const sx = (W - pad * 2) / Math.max(1, b.x1 - b.x0);
  const sz = (H - pad * 2) / Math.max(1, b.z1 - b.z0);
  const s = Math.min(sx, sz);
  const ox = pad + (W - pad * 2 - (b.x1 - b.x0) * s) / 2;
  const oz = pad + (H - pad * 2 - (b.z1 - b.z0) * s) / 2;
  const px = (x) => ox + (x - b.x0) * s;
  const pz = (z) => oz + (z - b.z0) * s;

  g.clearRect(0, 0, W, H);
  g.fillStyle = '#0a0d10';
  g.fillRect(0, 0, W, H);

  // open ground: the street strip, then alleys and yards
  const st = map.STREET;
  g.fillStyle = '#1b2127';
  if (st && st.kerb) {
    g.fillRect(px(-st.kerb), pz(st.zMin ?? b.z0), st.kerb * 2 * s, ((st.zMax ?? b.z1) - (st.zMin ?? b.z0)) * s);
  }
  for (const a of map.ALLEYS ?? []) {
    const [x0, z0, x1, z1] = a.rect;
    g.fillRect(px(x0), pz(z0), (x1 - x0) * s, (z1 - z0) * s);
  }

  // building footprints, taller ones lighter so the massing reads
  for (const bl of map.BUILDINGS ?? []) {
    const f = Math.min(4, bl.floors ?? 2);
    g.fillStyle = ['#2a3038', '#333b44', '#3d4650', '#48525e'][Math.max(0, f - 1)] ?? '#333b44';
    g.fillRect(px(bl.x - bl.w / 2), pz(bl.z - bl.d / 2), bl.w * s, bl.d * s);
  }

  // spawns
  g.fillStyle = '#7fd4c1';
  for (const sp of map.SPAWNS ?? []) {
    const [x, z] = sp;
    g.beginPath();
    g.arc(px(x), pz(z), 2.4, 0, Math.PI * 2);
    g.fill();
  }

  // frame
  g.strokeStyle = 'rgba(160,190,210,0.22)';
  g.lineWidth = 1;
  g.strokeRect(0.5, 0.5, W - 1, H - 1);
}

function planBounds(map) {
  if (Array.isArray(map.bounds) && map.bounds.length === 6) {
    return { x0: map.bounds[0], z0: map.bounds[2], x1: map.bounds[3], z1: map.bounds[5] };
  }
  let x0 = Infinity;
  let z0 = Infinity;
  let x1 = -Infinity;
  let z1 = -Infinity;
  for (const b of map.BUILDINGS ?? []) {
    x0 = Math.min(x0, b.x - b.w / 2);
    x1 = Math.max(x1, b.x + b.w / 2);
    z0 = Math.min(z0, b.z - b.d / 2);
    z1 = Math.max(z1, b.z + b.d / 2);
  }
  if (!Number.isFinite(x0)) return { x0: -60, z0: -60, x1: 60, z1: 60 };
  return { x0, z0, x1, z1 };
}

function hourLabel(hour) {
  if (hour == null) return '';
  const h = ((hour % 24) + 24) % 24;
  if (h < 5 || h >= 21) return 'NIGHT';
  if (h < 7.5) return 'DAWN';
  if (h < 11) return 'MORNING';
  if (h < 15.5) return 'MIDDAY';
  if (h < 18.5) return 'AFTERNOON';
  return 'DUSK';
}

/* ====================================================================== */
/* style                                                                  */
/* ====================================================================== */

const CSS = `
.rv-root{position:fixed;inset:0;z-index:60;display:flex;flex-direction:column;
  gap:clamp(10px,1.6vh,22px);padding:clamp(16px,3vh,44px) clamp(18px,4vw,64px);
  background:radial-gradient(120% 90% at 50% 0%,#0d1319 0%,#05070a 70%);
  color:#cfe0ea;font:400 13px/1.5 ui-sans-serif,system-ui,sans-serif;
  opacity:0;transition:opacity .22s ease}
.rv-root.rv-live{opacity:1}
/* The UA's [hidden]{display:none} is author-overridable, and the display:flex
   above outranks it — without this, hide() leaves a transparent z-index:60
   sheet across the whole viewport that silently eats every click on the menu
   and on the canvas (including the click that re-acquires pointer lock). The
   same guard exists on every other modal in this codebase. */
.rv-root[hidden]{display:none!important}
.rv-head{display:flex;align-items:flex-start;justify-content:space-between;gap:24px}
.rv-eyebrow{font:600 10px/1 ui-monospace,monospace;letter-spacing:.28em;color:#6f8fa3}
.rv-title{margin:.5em 0 .3em;font:700 clamp(20px,3.2vw,38px)/1 ui-sans-serif,system-ui,sans-serif;
  letter-spacing:.04em;color:#eaf3f8}
.rv-copy{margin:0;max-width:62ch;color:#8ea6b5;font-size:13px}
.rv-clock{display:flex;flex-direction:column;align-items:center;gap:6px;flex:0 0 auto}
.rv-clock-ring{--p:0;position:relative;width:64px;height:64px;border-radius:50%;
  display:grid;place-items:center;
  background:conic-gradient(#e0a33a calc(var(--p)*360deg),rgba(120,150,170,.16) 0);
  box-shadow:inset 0 0 0 6px #0a0f14}
.rv-clock-num{font:700 20px/1 ui-monospace,monospace;color:#f0d9a8}
.rv-clock-lbl{font:600 9px/1 ui-monospace,monospace;letter-spacing:.22em;color:#6f8fa3}
.rv-clock-idle .rv-clock-ring{background:rgba(120,150,170,.14)}
.rv-clock-idle .rv-clock-num{color:#5d7684}

.rv-filter{display:flex}
.rv-search{width:100%;max-width:420px;padding:9px 12px;border:1px solid rgba(140,175,195,.28);
  border-radius:2px;background:rgba(11,15,19,.7);color:#dceaf2;
  font:400 12px/1 ui-sans-serif,system-ui,sans-serif}
.rv-search::placeholder{color:#5d7684}
.rv-search:focus-visible{outline:2px solid #7fd4e8;outline-offset:1px}
.rv-no-matches{padding:18px 0;text-align:center;color:#6f8fa3;font:600 11px/1 ui-monospace,monospace;
  letter-spacing:.1em}

/* align-content:center keeps one row of cards optically centred instead of
   pinned under the header with the rest of the screen empty; the grid still
   wraps and scrolls once enough maps are registered to need a second row. */
/* align-items:start stops a card that wraps onto a short final row from being
   stretched to the row's height — an 'art' block with a fixed aspect ratio then
   leaves a tall empty gap under its text. */
.rv-grid{flex:1 1 auto;display:grid;gap:clamp(10px,1.4vw,20px);align-content:center;
  align-items:start;grid-template-columns:repeat(auto-fit,minmax(clamp(240px,19vw,330px),1fr));
  overflow:auto;padding:2px}
.rv-card{position:relative;display:flex;flex-direction:column;text-align:left;
  padding:0;border:1px solid rgba(140,175,195,.18);border-radius:3px;
  background:linear-gradient(180deg,rgba(20,27,33,.95),rgba(11,15,19,.95));
  color:inherit;font:inherit;cursor:pointer;overflow:hidden;
  transform:translateY(8px);opacity:0;animation:rv-rise .4s cubic-bezier(.2,.7,.2,1) forwards;
  animation-delay:calc(var(--i)*40ms);transition:border-color .16s,box-shadow .16s,transform .16s}
@keyframes rv-rise{to{transform:none;opacity:1}}
.rv-card:hover,.rv-card.rv-on{border-color:rgba(120,215,235,.65);
  box-shadow:0 0 0 1px rgba(120,215,235,.3),0 8px 30px -12px rgba(0,0,0,.9);transform:translateY(-2px)}
.rv-card:focus-visible{outline:2px solid #7fd4e8;outline-offset:2px}
.rv-card.rv-voted{border-color:#e0a33a;box-shadow:0 0 0 1px rgba(224,163,58,.45)}
.rv-card.rv-won{border-color:#7fd4c1;box-shadow:0 0 0 2px rgba(127,212,193,.5)}

.rv-art{position:relative;overflow:hidden;background:#080b0e;border-bottom:1px solid rgba(140,175,195,.14)}
.rv-plan{display:block;width:100%;height:auto;aspect-ratio:16/10}
/* Covers the plan exactly. object-fit:cover because the source is cropped to
   16:10 already — this only absorbs sub-pixel rounding on odd card widths. */
.rv-photo{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;
  opacity:0;transition:opacity .25s ease,transform .3s cubic-bezier(.2,.7,.2,1)}
.rv-photo.rv-photo-on{opacity:1}
.rv-card:hover .rv-photo,.rv-card.rv-on .rv-photo{transform:scale(1.045)}
.rv-hour,.rv-loaded{position:absolute;top:7px;font:600 9px/1 ui-monospace,monospace;
  letter-spacing:.18em;padding:3px 6px;border-radius:2px;background:rgba(6,9,12,.82)}
.rv-hour{left:7px;color:#9fb8c6}
.rv-loaded{right:7px;color:#7fd4c1;border:1px solid rgba(127,212,193,.4)}
.rv-dice{aspect-ratio:16/10;display:grid;place-items:center;
  font:700 44px/1 ui-monospace,monospace;color:#3c4854}

.rv-body{display:flex;flex-direction:column;gap:5px;padding:10px 12px 12px}
.rv-card-top{display:flex;align-items:baseline;justify-content:space-between;gap:8px;min-width:0}
.rv-name{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font:700 15px/1 ui-sans-serif,system-ui,sans-serif;letter-spacing:.06em;color:#eaf3f8}
.rv-size{flex:none;font:600 9px/1 ui-monospace,monospace;letter-spacing:.16em;color:#6f8fa3}
.rv-theme{color:#a9c0cd;font-size:12px}
.rv-play{color:#7e94a2;font-size:11.5px}
.rv-tally{height:3px;margin-top:6px;background:rgba(140,175,195,.14);border-radius:2px;overflow:hidden}
.rv-tally>i{display:block;height:100%;width:0;background:#e0a33a;transition:width .3s ease}
.rv-card.rv-lead .rv-tally>i{background:#7fd4c1}
.rv-tally-off{display:none}
.rv-count{font:600 9px/1 ui-monospace,monospace;letter-spacing:.16em;color:#8ea6b5;min-height:9px}

.rv-foot{display:flex;align-items:center;gap:16px}
.rv-back{padding:9px 16px;border:1px solid rgba(140,175,195,.28);border-radius:2px;
  background:transparent;color:#9fb8c6;font:600 11px/1 ui-monospace,monospace;
  letter-spacing:.18em;text-transform:uppercase;cursor:pointer}
.rv-back:hover{border-color:rgba(140,175,195,.6);color:#dceaf2}
.rv-confirm{margin-left:auto;padding:11px 26px;border:0;border-radius:2px;
  background:#e0a33a;color:#12181d;font:700 12px/1 ui-monospace,monospace;
  letter-spacing:.2em;cursor:pointer}
.rv-confirm:hover{background:#f0b64c}
.rv-hint{display:flex;align-items:center;gap:7px;color:#6f8fa3;
  font:600 9px/1 ui-monospace,monospace;letter-spacing:.16em}
.rv-key{display:inline-grid;place-items:center;min-width:18px;height:18px;padding:0 4px;
  border:1px solid rgba(140,175,195,.3);border-radius:2px;color:#9fb8c6}
@media(max-width:720px){
  .rv-root{padding:14px 12px;gap:10px}.rv-copy{font-size:11px}.rv-clock-ring{width:48px;height:48px}
  .rv-grid{grid-template-columns:1fr;align-content:start}.rv-card{display:grid;grid-template-columns:minmax(120px,40%) 1fr}
  .rv-art{border-bottom:0;border-right:1px solid rgba(140,175,195,.14)}.rv-body{min-width:0}
  .rv-hint{display:none}.rv-foot{flex-wrap:wrap}.rv-confirm{margin-left:auto}
}
@media (prefers-reduced-motion:reduce){
  .rv-root,.rv-card{transition:none;animation:none;opacity:1;transform:none}
  /* The art still has to fade from 0, or it would never become visible — but it
     does so instantly, and the hover push-in is dropped entirely. */
  .rv-photo{transition:none}
  .rv-card:hover .rv-photo,.rv-card.rv-on .rv-photo{transform:none}
}
`;
